import { useEffect, useMemo, useState } from 'react';
import Cookies from 'universal-cookie';

import SearchableStringListDropdown from './Filters/SearchableStringListDropdown';
import Project from '../types/Dashboard/Project';

const cookies = new Cookies();

type Props = {
    /** List of projects to display in the dropdown */
    projects: Project[] | undefined;
    /** 
     * Cookie name used to persist the selection. 
     * Pages that should share the same selection should use the same cookieName.
     * e.g., 'projectGoals' for all Project Goals pages, 'dataSource' for Data Source pages
     */
    cookieName: string;
    /** 
     * Called when the user selects a project.
     * Receives the projectID of the selected project, or undefined if cleared.
     */
    onProjectChange?: (projectId: string | undefined) => void;
    /** Optional label for the dropdown (default: "Project") */
    label?: string;
    /** Optional placeholder when no project is selected */
    placeholder?: string;
};

/**
 * A project dropdown component that persists selection via cookies.
 * 
 * Multiple instances with the same `cookieName` will share the same selection.
 * When a user navigates between pages that use the same cookieName, 
 * the selected project will be preserved.
 * 
 * @example
 * // On Dashboard page
 * <ProjectDropdown 
 *   projects={projectList} 
 *   cookieName="mainProjects" 
 *   onProjectChange={handleProjectChange} 
 * />
 * 
 * // On Quality Control page (shares selection with Dashboard)
 * <ProjectDropdown 
 *   projects={projectList} 
 *   cookieName="mainProjects" 
 *   onProjectChange={handleProjectChange} 
 * />
 */
const ProjectDropdown = ({
    projects,
    cookieName,
    onProjectChange,
    label = 'Project',
    placeholder = 'Select a project...'
}: Props) => {
    // Initialize from cookie (only if cookieName is valid)
    const [selectedProjectId, setSelectedProjectId] = useState<string | undefined>(() => {
        if (!cookieName) return undefined;
        const value = cookies.get(cookieName);
        // Cookie library returns numbers as numbers, so convert to string
        return value !== undefined ? String(value) : undefined;
    });

    // Build and sort options: "1234 - Project Name"
    const projectOptions = useMemo(() => {
        if (!projects) return [];
        return [...projects]
            .sort((a, b) => parseInt(a.projectNumber) - parseInt(b.projectNumber))
            .map((p) => ({
                label: `${p.projectNumber} - ${p.projectName}`,
                value: p.projectID
            }));
    }, [projects]);

    // Dropdown expects an array of strings (labels)
    const optionLabels = useMemo(() => projectOptions.map(p => p.label), [projectOptions]);

    // Get the label for the current selection
    const selectedLabel = useMemo(() => 
        projectOptions.find(opt => opt.value === selectedProjectId)?.label
    , [projectOptions, selectedProjectId]);

    // When projects load, verify the selected project exists and notify parent
    useEffect(() => {
        if (!selectedProjectId) return;
        
        // Once projects are loaded, verify the selection exists
        if (projects && projects.length > 0) {
            const projectExists = projects.some(p => p.projectID === selectedProjectId);
            if (projectExists) {
                onProjectChange?.(selectedProjectId);
            } else {
                // Stored project no longer valid, clear it
                setSelectedProjectId(undefined);
                if (cookieName) {
                    cookies.remove(cookieName, { path: '/' });
                }
                onProjectChange?.(undefined);
            }
        } else {
            // Projects not loaded yet, but we have a selected ID - notify parent
            onProjectChange?.(selectedProjectId);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projects, cookieName]);

    // Handle user selection
    const handleChange = (label: string | undefined) => {
        const selected = projectOptions.find(opt => opt.label === label);
        const newProjectId = selected?.value;

        setSelectedProjectId(newProjectId);
        
        // Persist to cookie (only if cookieName is valid)
        if (cookieName) {
            if (newProjectId) {
                cookies.set(cookieName, newProjectId, { path: '/', maxAge: 2592000, sameSite: 'lax' }); // 30 days
            } else {
                cookies.remove(cookieName, { path: '/' });
            }
        }
        
        onProjectChange?.(newProjectId);
    };

    return (
        <SearchableStringListDropdown
            options={optionLabels}
            defaultValue={selectedLabel}
            onChange={handleChange}
            label={label}
            placeholder={placeholder}
            inputPlaceholder="Type to search…"
            noResultsText="No matching projects"
        />
    );
};

export default ProjectDropdown;
